import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Wallet, CheckCircle } from 'lucide-react';
import { patientService } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const STATUS_LABELS: Record<string, string> = {
  brouillon: 'Brouillon',
  emise: 'Émise',
  payee: 'Payée',
  partiellement_payee: 'Partiellement payée',
  prise_en_charge_assurance: 'Prise en charge assurance',
  annulee: 'Annulée',
};

const CompteClientTab: React.FC<{ patientId: number }> = ({ patientId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['patient', 'compte-tiers', patientId],
    queryFn: () => patientService.getCompteTiers(patientId).then((r) => r.data),
  });

  if (isLoading || !data) {
    return <div className="text-gray-500">Chargement du compte client…</div>;
  }

  const b = data.balance_agee;
  const solde = num(data.solde);

  return (
    <div className="space-y-6">
      {/* Synthèse */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={`rounded-xl p-4 ${solde > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
          <div className="flex items-center gap-2">
            <Wallet className={`h-5 w-5 ${solde > 0 ? 'text-red-600' : 'text-green-600'}`} />
            <span className="text-sm text-gray-600">Solde restant dû</span>
          </div>
          <p className={`text-2xl font-bold mt-1 ${solde > 0 ? 'text-red-700' : 'text-green-700'}`}>
            {formatCurrency(solde)}
          </p>
        </div>
        <div className="rounded-xl p-4 bg-gray-50">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-gray-500" />
            <span className="text-sm text-gray-600">Total déjà payé</span>
          </div>
          <p className="text-2xl font-bold mt-1 text-gray-800">{formatCurrency(num(data.total_paye))}</p>
        </div>
      </div>

      {/* Balance âgée */}
      {solde > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
            <Clock className="h-4 w-4" /> Balance âgée (ancienneté de la créance)
          </h4>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { k: '0_30', label: '0-30 j' },
              { k: '31_60', label: '31-60 j' },
              { k: '61_90', label: '61-90 j' },
              { k: 'plus_90', label: '+90 j' },
            ].map((bucket) => (
              <div key={bucket.k} className="bg-gray-50 rounded-lg p-2">
                <p className="text-xs text-gray-400">{bucket.label}</p>
                <p className="text-sm font-semibold text-gray-800">{formatCurrency(num((b as any)?.[bucket.k]))}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Détail des factures */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h4 className="font-semibold text-gray-900">Factures ({data.factures.length})</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">N°</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Statut</th>
                <th className="px-4 py-2 text-right">Montant</th>
                <th className="px-4 py-2 text-right">Payé</th>
                <th className="px-4 py-2 text-right">Reste</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.factures.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-700">{f.numero}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">{f.date_emission}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{STATUS_LABELS[f.status] || f.status}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-700">{formatCurrency(num(f.montant_patient))}</td>
                  <td className="px-4 py-2 text-sm text-right text-green-700">{formatCurrency(num(f.montant_paye))}</td>
                  <td className={`px-4 py-2 text-sm text-right font-semibold ${num(f.reste_a_payer) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {formatCurrency(num(f.reste_a_payer))}
                  </td>
                </tr>
              ))}
              {data.factures.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucune facture.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompteClientTab;
